import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import LiveApy from "../components/LiveApy";
import BankMarquee from "../components/BankMarquee";
import { useState } from "react";
import { subscribeEmail } from "../api/subscribe";


const faq = [
  { q: "Is CRAM Finance really free?", a: "Yes, completely. We earn through affiliate partnerships, which never affects our rankings or which products we recommend." },
  { q: "How do you choose what to recommend?", a: "We score accounts on fees, ATM access, student perks, mobile UX and ease of use — not on commission size." },
  { q: "Do I have to be a student to use the platform?", a: "Not at all. We focus on students, but anyone who wants to simplify their finances is welcome." },
];

export default function Home() {
  const [captureEmail, setCaptureEmail] = useState("");
  const [captureStatus, setCaptureStatus] = useState("idle");
  const [captureErrorMsg, setCaptureErrorMsg] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleEmailCapture = async (e) => {
    e.preventDefault();
    const trimmedEmail = captureEmail.trim();

    if (!trimmedEmail || !trimmedEmail.includes("@")) {
      setCaptureStatus("error");
      setCaptureErrorMsg("Please enter a valid email.");
      return;
    }

    setCaptureStatus("sending");
    setCaptureErrorMsg("");

    try {
      await subscribeEmail(trimmedEmail);
      setCaptureStatus("success");
      setCaptureEmail("");
    } catch (error) {
      setCaptureStatus("error");
      setCaptureErrorMsg(error.message || "Something went wrong.");
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();

    const trimmedEmail = email.trim();
    if (!trimmedEmail || !trimmedEmail.includes("@")) {
      setStatus("error");
      setErrorMsg("Please enter a valid email.");
      return;
    }

    setStatus("loading");
    setErrorMsg("");

    try {
      await subscribeEmail(trimmedEmail);
      setStatus("success");
      setEmail("");
    } catch (error) {
      setStatus("error");
      setErrorMsg(error.message || "Something went wrong.");
    }
  };

  return (
    <main data-testid="page-home">
      {/* HERO */}
      <section
        style={{
          position: "relative",
          padding: "140px 0 80px",
          overflow: "hidden",
        }}
        className="noise-overlay"
      >
        <div
          style={{
            position: "absolute",
            inset: "-20% -10% auto auto",
            width: 640,
            height: 640,
            background: "radial-gradient(closest-side, rgba(20, 163, 122, 0.25), transparent 70%)",
            filter: "blur(12px)",
            pointerEvents: "none",
          }}
        />
        <div
          className="container hero-grid"
          style={{
            position: "relative",
            display: "grid",
            gridTemplateColumns: "1.1fr 1fr",
            gap: 80,
            alignItems: "center",
          }}
        >
          <div className="hero-content">
            <span className="eyebrow">Student finance, simplified</span>
            <h1
              style={{
                fontSize: "clamp(46px, 6vw, 80px)",
                lineHeight: 0.98,
                margin: "26px 0 22px",
                letterSpacing: "-0.03em",
              }}
            >
              Finding the best finance tools,{" "}
              <em style={{ fontStyle: "italic", color: "var(--emerald)" }}>the easy way</em>.
            </h1>
            <p style={{ fontSize: 18.5, color: "var(--t2)", maxWidth: 560, marginBottom: 32, lineHeight: 1.55 }}>
              CRAM Finance shows students the best student bank accounts, budgeting apps, credit cards, and other financial tools. No ads. No bias. Just clear answers based on real research.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 36 }}>
              <Link to="/accounts" className="btn btn--primary" data-testid="hero-cta-accounts">
                Find bank accounts <ArrowRight size={16} className="arrow" />
              </Link>
              <Link to="/apps" className="btn btn--ghost" data-testid="hero-cta-apps">
                Explore money apps
              </Link>
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 28, color: "var(--t3)", fontSize: 14, marginBottom: 18 }}>
              <Stat label="monthly fees" value="$0" />
              <Stat label="products reviewed" value="40+" />
              <Stat label="free, always" value="100%" />
            </div>

            {captureStatus === "success" ? (
              <div style={{ marginBottom: 32, color: "var(--emerald)", fontSize: 14 }}>
                You're in. We'll hit you when something new drops.
              </div>
            ) : (
              <form onSubmit={handleEmailCapture} style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", marginBottom: 32 }}>
                <div className="font-mono" style={{ fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase", color: "var(--t3)", marginBottom: 10 }}>
                  Get notified of website updates
                </div>
                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <input
                    className="input"
                    type="email"
                    value={captureEmail}
                    onChange={(e) => {
                      setCaptureEmail(e.target.value);
                      if (captureStatus !== "idle") {
                        setCaptureStatus("idle");
                        setCaptureErrorMsg("");
                      }
                    }}
                    placeholder="you@example.com"
                    style={{ flex: 1, minWidth: 220, maxWidth: 340 }}
                  />
                  <button type="submit" className="btn btn--primary" disabled={captureStatus === "sending"}>
                    {captureStatus === "sending" ? "Joining..." : "Notify me"}
                  </button>
                </div>
                {captureStatus === "error" && (
                  <div style={{ color: "var(--rust)", fontSize: 14, marginTop: 10 }}>
                    {captureErrorMsg || "Couldn't save that — contact support for help."}
                  </div>
                )}
              </form>
            )}
          </div>

          {/* Right visual */}
          <div style={{ position: "relative", minHeight: 540 }} className="hero-visual">
            <div
              className="hero-visual__apy"
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                width: "min(360px, 100%)",
                transform: "rotate(2deg)",
              }}
            >
              <LiveApy />
            </div>

            {/* Editor's pick card */}
            <div
              className="hero-visual__pick"
              style={{
                position: "absolute",
                left: -10,
                top: 250,
                width: 320,
                background: "var(--bg-elev)",
                border: "1px solid var(--line)",
                borderRadius: 28,
                padding: 24,
                boxShadow: "var(--shadow-lg)",
                transform: "rotate(-3deg)",
              }}
            >
              <span className="tag tag--gold">Editor's pick</span>
              <h3
                style={{
                  fontFamily: "Outfit, sans-serif",
                  fontSize: 19,
                  fontWeight: 600,
                  marginTop: 14,
                  marginBottom: 16,
                }}
              >
                Chase Secure Banking
              </h3>
              <Row k="Monthly fee" v="$4.95" />
              <Row k="Sign-up bonus" v="$175" />
              <Row k="ATM network" v="15,000+" />
              <div
                style={{
                  marginTop: 16,
                  paddingTop: 14,
                  borderTop: "1px solid var(--line)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span className="font-mono" style={{ fontSize: 11, color: "var(--t3)", textTransform: "uppercase", letterSpacing: "0.15em" }}>
                  Match score
                </span>
                <span style={{ fontFamily: "Fraunces, serif", fontSize: 30, color: "var(--emerald)" }}>88<span style={{ fontSize: 14, color: "var(--t3)" }}>/100</span></span>
              </div>
            </div>

            {/* small floating chip */}
            <div
              className="hero-visual__chip"
              style={{
                position: "absolute",
                left: 240,
                bottom: 20,
                background: "var(--ink-deep)",
                color: "var(--t-on-dark)",
                padding: "10px 16px",
                borderRadius: 999,
                fontSize: 13,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                boxShadow: "var(--shadow)",
              }}
            >
              <span className="live-dot" /> 3 new picks today
            </div>
          </div>
        </div>

        <style>{`
          @media (max-width: 1000px) {
            .hero-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
            .hero-content { text-align: left; }
          }
          @media (max-width: 900px) {
            /* Switch the hero visual from absolute-overlap to a clean stack */
            .hero-visual {
              min-height: 0 !important;
              display: flex !important;
              flex-direction: column !important;
              gap: 22px !important;
              align-items: center !important;
              padding: 8px 4px 12px !important;
            }
            .hero-visual__apy,
            .hero-visual__pick,
            .hero-visual__chip {
              position: static !important;
              left: auto !important;
              right: auto !important;
              top: auto !important;
              bottom: auto !important;
            }
            .hero-visual__apy {
              width: 100% !important;
              max-width: 380px !important;
              transform: rotate(1.5deg) !important;
            }
            .hero-visual__pick {
              width: 100% !important;
              max-width: 340px !important;
              transform: rotate(-2deg) !important;
            }
            .hero-visual__chip {
              transform: none !important;
              align-self: center !important;
            }
          }
        `}</style>
      </section>

      <BankMarquee />


      {/* WHY */}
      <section style={{ padding: "100px 0" }}>
        <div className="container">
          <div style={{ maxWidth: 720, marginBottom: 56 }}>
            <span className="eyebrow">Why CRAM?</span>
            <h2 style={{ fontSize: "clamp(36px, 4vw, 54px)", marginTop: 20, marginBottom: 16 }}>
              Figuring out your finances shouldn't be a  <em style={{ fontStyle: "italic", color: "var(--emerald)" }}>research project</em>.
            </h2>
            <p style={{ fontSize: 17, color: "var(--t2)" }}>
              Most students and young adults may get overwhelmed when trying to figure out their finances (e.g. finding the best bank accounts, credit cards). 
              At CRAM Finance, we provide you with the information and tools necessary to make this process as easy as possible.
            </p>
          </div>

          <div
            style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}
            className="pillars-grid"
          >
            <Pillar n="01" t="Detailed information" d="Breakdowns of the fees, perks and limits that actually matter to a student budget." />
            <Pillar n="02" t="Student-first picks" d="Every recommendation is catered to students and students only — no commission incentives." />
            <Pillar n="03" t="Free tools" d='No paywalls, no subscriptions, no "premium tier." Every single tool on CRAM Finance is free to use, always.' />
          </div>
        </div>
        <style>{`@media (max-width: 800px){ .pillars-grid { grid-template-columns: 1fr !important; } }`}</style>
      </section>

      {/* TOP PICKS */}
      <section style={{ padding: "80px 0", background: "var(--bg-warm)" }} className="noise-overlay">
        <div className="container">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 56, flexWrap: "wrap", gap: 24 }}>
            <div style={{ maxWidth: 600 }}>
              <span className="eyebrow">Top bank account picks</span>
              <h2 style={{ fontSize: "clamp(34px, 3.6vw, 50px)", marginTop: 20, marginBottom: 14 }}>
                The two accounts most students should start with.
              </h2>
              <p style={{ fontSize: 16, color: "var(--t2)" }}>
                We compared 40+ accounts on fees, ATM reach, sign-up bonuses, mobile UX and student perks. Two stood
                out as a near-universal fit.
              </p>
            </div>
            <Link to="/accounts" className="btn btn--ghost" data-testid="picks-cta-all">
              See all top accounts <ArrowRight size={15} className="arrow" />
            </Link>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }} className="top-picks-grid">
            <PickCard
              slug="chase-secure-banking"
              label="Editor's pick"
              name="Chase Secure Banking"
              detail="No overdraft fees · $175 bonus · 15,000+ ATMs"
              tag="Best for beginners"
            />
            <PickCard
              slug="sofi-checking-savings"
              label="Free & online"
              name="SoFi Checking & Savings"
              detail="No fees · 3.80% APY · Early payday"
              tag="Best digital account"
              highlight
            />
          </div>
        </div>
        <style>{`@media (max-width: 800px){ .top-picks-grid { grid-template-columns: 1fr !important; } }`}</style>
      </section>

      {/* STAT STRIP */}
      <section style={{ background: "var(--ink-deep)", color: "var(--t-on-dark)", padding: "0", overflow: "hidden" }} className="noise-overlay">
        <div className="container">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              padding: "0",
            }}
            className="stat-strip"
          >
            <StatCell big="$0" label="Monthly fees on every top pick we recommend." />
            <StatCell big={<><em style={{ fontStyle: "italic", color: "var(--emerald-2)" }}>5</em> min</>} label="Average time to find an account that fits your life." />
            <StatCell big="40+" label="Accounts, cards and apps reviewed for students." last />
          </div>
        </div>
        <style>{`@media (max-width: 800px){ .stat-strip { grid-template-columns: 1fr !important; } }`}</style>
      </section>

      {/* FAQ */}
      <section style={{ padding: "100px 0" }}>
        <div className="container-narrow">
          <span className="eyebrow">FAQ</span>
          <h2 style={{ fontSize: "clamp(34px, 3.6vw, 50px)", marginTop: 20, marginBottom: 36 }}>Quick answers.</h2>
          <Faq items={faq} />
        </div>
      </section>

      {/* CTA Banner */}
      <section style={{ padding: "60px 0 100px" }}>
        <div className="container">
          <div
            className="noise-overlay"
            style={{
              background: "var(--ink-deep)",
              borderRadius: 32,
              padding: "clamp(48px, 7vw, 96px)",
              textAlign: "center",
              color: "var(--t-on-dark)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: "auto auto -50% -10%",
                width: 480,
                height: 480,
                borderRadius: "50%",
                background: "radial-gradient(closest-side, rgba(20, 163, 122, 0.35), transparent)",
                filter: "blur(10px)",
              }}
            />
            <div
              style={{
                position: "absolute",
                inset: "-50% -10% auto auto",
                width: 380,
                height: 380,
                borderRadius: "50%",
                background: "radial-gradient(closest-side, rgba(255, 209, 102, 0.3), transparent)",
                filter: "blur(10px)",
              }}
            />
            <div style={{ position: "relative" }}>
              <span className="eyebrow" style={{ background: "rgba(244, 241, 232, 0.1)", color: "var(--gold)", borderColor: "rgba(255, 209, 102, 0.3)" }}>
                To start off...
              </span>
              <h2 style={{ fontSize: "clamp(34px, 5vw, 64px)", color: "var(--t-on-dark)", marginTop: 22, marginBottom: 14 }}>
                Find <em style={{ fontStyle: "italic", color: "var(--gold)" }}>your</em> dream bank account within <em style={{ fontStyle: "italic", color: "var(--gold)" }}>five minutes</em>.
              </h2>
              <p style={{ color: "var(--t-on-dark-2)", marginBottom: 32, fontSize: 17, maxWidth: 540, margin: "0 auto 32px" }}>
                No sign-up. No spam. Just clear picks for your campus life.
              </p>
              <Link to="/accounts" className="btn btn--gold" data-testid="bottom-cta">
                Bank Accounts <ArrowRight size={16} className="arrow" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function Stat({ label, value }) {
  return (
    <span>
      <strong style={{ color: "var(--t1)", fontWeight: 600 }}>{value}</strong>{" "}
      <span className="font-mono" style={{ fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--t3)" }}>
        {label}
      </span>
    </span>
  );
}

function Row({ k, v }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "9px 0",
        borderTop: "1px solid var(--line)",
        fontSize: 14,
        color: "var(--t2)",
      }}
    >
      <span>{k}</span>
      <span className="font-mono" style={{ color: "var(--t1)", fontWeight: 500 }}>{v}</span>
    </div>
  );
}

function Pillar({ n, t, d }) {
  return (
    <div className="card-base" style={{ padding: 32 }}>
      <span
        className="font-mono"
        style={{ fontSize: 11, letterSpacing: "0.22em", color: "var(--emerald)", textTransform: "uppercase" }}
      >
        {n}
      </span>
      <h3 style={{ fontFamily: "Outfit, sans-serif", fontSize: 22, fontWeight: 600, margin: "16px 0 10px", color: "var(--t1)" }}>{t}</h3>
      <p style={{ fontSize: 15, color: "var(--t2)", margin: 0, lineHeight: 1.6 }}>{d}</p>
    </div>
  );
}

function PickCard({ slug, label, name, detail, tag, highlight }) {
  return (
    <Link
      to={`/accounts/${slug}`}
      data-testid={`top-pick-${slug}`}
      className="card-base"
      style={{
        padding: 32,
        display: "block",
        background: highlight ? "linear-gradient(135deg, #fffdf7 0%, var(--emerald-soft) 100%)" : "var(--bg-elev)",
      }}
    >
      <span className="font-mono" style={{ fontSize: 11, letterSpacing: "0.2em", color: "var(--emerald)", textTransform: "uppercase" }}>
        {label}
      </span>
      <h3 style={{ fontFamily: "Fraunces, serif", fontSize: 32, marginTop: 12, marginBottom: 10, color: "var(--t1)" }}>
        {name}
      </h3>
      <p style={{ color: "var(--t2)", marginBottom: 20, fontSize: 15 }}>{detail}</p>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span className="tag tag--soft">{tag}</span>
        <ArrowUpRight size={20} color="var(--emerald)" />
      </div>
    </Link>
  );
}

function StatCell({ big, label, last }) {
  return (
    <div style={{ padding: "56px 32px", borderRight: last ? "none" : "1px solid rgba(244, 241, 232, 0.12)" }}>
      <div style={{ fontFamily: "Fraunces, serif", fontSize: "clamp(48px, 6vw, 80px)", lineHeight: 1, color: "var(--t-on-dark)", marginBottom: 14 }}>{big}</div>
      <div style={{ color: "var(--t-on-dark-2)", fontSize: 15, maxWidth: 280, lineHeight: 1.5 }}>{label}</div>
    </div>
  );
}

function Faq({ items }) {
  const [open, setOpen] = useState(-1);
  return (
    <div>
      {items.map((it, i) => (
        <div key={i} style={{ borderTop: "1px solid var(--line)", ...(i === items.length - 1 ? { borderBottom: "1px solid var(--line)" } : {}) }}>
          <button
            data-testid={`faq-q-${i}`}
            onClick={() => setOpen(open === i ? -1 : i)}
            style={{
              width: "100%",
              textAlign: "left",
              padding: "24px 0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 24,
              background: "transparent",
              border: "none",
              cursor: "pointer",
              fontSize: 18,
              fontWeight: 500,
              color: "var(--t1)",
            }}
          >
            <span>{it.q}</span>
            <span
              style={{
                width: 30,
                height: 30,
                borderRadius: "50%",
                background: "var(--emerald-soft)",
                color: "var(--emerald)",
                display: "grid",
                placeItems: "center",
                fontSize: 18,
                transform: open === i ? "rotate(45deg)" : "rotate(0)",
                transition: "transform 0.25s var(--ease)",
              }}
            >
              +
            </span>
          </button>
          <div
            style={{
              maxHeight: open === i ? 220 : 0,
              overflow: "hidden",
              transition: "max-height 0.3s var(--ease), padding 0.25s var(--ease)",
              color: "var(--t2)",
              fontSize: 16,
              paddingBottom: open === i ? 24 : 0,
            }}
          >
            {it.a}
          </div>
        </div>
      ))}
    </div>
  );
}
